import { InfoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/common/components/ui/button';
import { Textarea } from '@/common/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/common/components/ui/tooltip';

interface ErrorDescriptionPanelProps {
  description: string;
  onDescriptionChange: (description: string) => void;
  onCancel: () => void;
  onSave: () => void;
}

export const ErrorDescriptionPanel = ({
  description,
  onDescriptionChange,
  onCancel,
  onSave,
}: ErrorDescriptionPanelProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col mt-4">
      <div className="flex items-center gap-x-1">
        <p className="text-sm font-medium mb-2">
          {t('coverage.addDescription')}
        </p>
        <p className="text-xs text-muted-foreground mb-2">
          ({t('coverage.required')})
        </p>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center mb-2">
                <InfoIcon className="w-[16px] h-[16px] text-muted-foreground" />
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <div className="space-y-2 text-xs font-light">
                <p>{t('coverage.addDescriptionTooltip.intro')}</p>

                <ul className="list-disc pl-4 space-y-1">
                  {t('coverage.addDescriptionTooltip.items', {
                    returnObjects: true,
                  }).map((item: string) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>

                <p className="pt-1">
                  {t('coverage.addDescriptionTooltip.outro')}
                </p>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <Textarea
        value={description}
        onChange={(e) => onDescriptionChange(e.target.value)}
        placeholder={t('coverage.descriptionPlaceholder')}
      />

      <div className="flex justify-end gap-x-3 mt-4">
        <Button variant="outline" onClick={onCancel}>
          {t('actions.cancel')}
        </Button>
        <Button
          variant="secondary"
          onClick={(e) => {
            e.stopPropagation();
            onSave();
          }}
        >
          {t('actions.save')}
        </Button>
      </div>
    </div>
  );
};
